import { FormControl, PopperProps, styled } from '@mui/material';
import {
  DatePicker as MUIDatePicker,
  DatePickerProps as MUIDatePickerProps,
} from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { formatDate } from '@verantos-dev/shared-utils';

import { getWidth } from './helper/getWidth';
dayjs.extend(utc);

interface LocalDatePickerProps {
  /**
   * @prop {string} id - id of the entry, also used are the key
   */
  id?: string;

  /**
   * @prop {string} displayLabel
   */
  displayLabel: string;

  /**
   * @prop {'xxlarge' | 'xlarge' | 'large' | 'medium' | 'small' |  'auto' | number} width - the width of the dropdown
   * @default 'medium'
   */
  width?: 'xxlarge' | 'xlarge' | 'large' | 'medium' | 'small' | 'dialog' | 'auto' | number;

  /**
   * @prop {string | null} value - the value of the selected item
   * @default null
   */
  value?: Dayjs | null;

  /**
   * @prop {Dayjs} minDate - the minimum date
   * @default null
   */
  minDate?: Dayjs | null;

  /**
   * @prop {Dayjs} maxDate - the maximum date
   * @default null
   */
  maxDate?: Dayjs | null;

  /**
   * @prop {boolean} inError - if the input is in error
   * @default false
   */
  inError?: boolean;

  /**
   * @prop {string | null} helperText - the helper text
   * @default null
   */
  helperText?: string | null;

  /**
   * @prop {boolean} disableFuture - disable future dates
   * @default false
   */
  disableFuture?: boolean;

  /**
   * @prop {callback} onChange - click handler
   * @param {string} id - id of the button
   * @param {Dayjs | null} val - the selected value
   */
  onChange?: (id: string, val: Dayjs | null) => void;

  /**
   * The position of the tooltip
   * @default bottom-end
   */
  placement?: PopperProps['placement'];

  /**
   * @prop {callback} onError - error handler
   * @param {string} id - id of the button
   * @param {string | null} reason - the reason for the error
   */
  onError?: (id: string, reason: string | null) => void;

  /**
   * @prop {boolean} viewMode - To display the date picker in the view mode
   * @default false
   * */
  viewMode?: boolean;
}

// props for the date picker component
export type DatePickerProps = LocalDatePickerProps &
  Omit<
    MUIDatePickerProps<Dayjs>,
    | 'label'
    | 'displayEmpty'
    | 'notched'
    | 'renderValue'
    | 'value'
    | 'onChange'
    | 'onError'
    | 'disableFuture'
    | 'minDate'
    | 'maxDate'
  >;

const DatePickerViewMode = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  fontFamily: 'Roboto, sans-serif',
  '& .-label': {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '24px',
    color: '#666',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical',
  },
  '& .-value': {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
    color: '#252a36',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
});

export function DatePicker(props: DatePickerProps) {
  const {
    id = 'datepicker',
    displayLabel,
    value,
    width = 'medium',
    disableFuture,
    minDate,
    maxDate,
    inError,
    helperText,
    placement = 'bottom-end',
    onChange,
    onError,
    viewMode = false,
    ...rest
  } = props;

  const onChangeDatePicker = (value: unknown) => {
    onChange && onChange(id, value as Dayjs | null);
  };

  const onErrorHandler = (reason: unknown) => {
    onError && onError(id, reason as string | null);
  };

  return (
    <DatePickerViewMode data-testid={id}>
      {viewMode ? (
        <FormControl id={`datepicker-formControl-${id}`} variant="outlined" sx={{ m: 0 }}>
          <DatePickerViewMode
            id={`datePicker-${id}-container-viewMode`}
            data-testid={`date-picker-view-mode-${id}`}
          >
            <div className="-label">{displayLabel}</div>
            {value && <div className="-value">{formatDate(value.toString(), 'dateNumeric')}</div>}
          </DatePickerViewMode>
        </FormControl>
      ) : (
        <FormControl
          id={`datepicker-formControl-${id}`}
          variant="outlined"
          sx={{ m: 0, width: getWidth(width) }}
        >
          <MUIDatePicker
            {...rest}
            data-testid={`datepicker-${id}`}
            label={displayLabel}
            value={value}
            disableFuture={disableFuture}
            minDate={minDate ?? undefined}
            maxDate={maxDate ?? undefined}
            views={['year', 'month', 'day']}
            onChange={onChangeDatePicker}
            onError={onErrorHandler}
            timezone="UTC"
            slotProps={{
              popper: { placement: placement },
              textField: {
                InputLabelProps: { shrink: true },
                error: inError ? true : undefined,
                helperText: helperText,
              },
              desktopPaper: {
                sx: {
                  border: '2px solid #d3d4d7',
                  boxShadow:
                    '0px 1px 18px rgba(0, 0, 0, 0.12), 0px 6px 10px rgba(0, 0, 0, 0.14), 0px 3px 5px -1px rgba(0, 0, 0, 0.2)',
                },
              },
            }}
          />
        </FormControl>
      )}
    </DatePickerViewMode>
  );
}

export default DatePicker;
